<template>
    <TransitionRoot
        as="template"
        :show="state.open"
    >
        <Dialog
            as="div"
            class="relative z-6000"
            :open="state.open === true"
            @close="dialogClose(state.overlayPreventClose)"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div
                    class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                        enter-to="opacity-100 translate-y-0 scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                    >
                        <DialogPanel
                            class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6"
                        >
                            <div class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                                <button
                                    type="button"
                                    class="cursor-pointer rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden"
                                    @click="dialogClose()"
                                >
                                    <span class="sr-only">Close</span>
                                    <MdiClose
                                        class="h-6 w-6"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                            <div class="select-none sm:flex sm:items-start">
                                <div
                                    class="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-200 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <MdiChevronDoubleDown
                                        class="h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <DialogTitle
                                        as="h3"
                                        class="text-lg leading-6 font-medium text-gray-900"
                                    >
                                        {{ t('placeholderNewKey') }}
                                    </DialogTitle>
                                    <div class="mt-2">
                                        <label
                                            for="newkey"
                                            class="block text-sm font-medium text-gray-700"
                                        >
                                            <span v-if="conflict === true">{{
                                                t('palceholderKeyExists')
                                            }}</span>
                                            <span v-if="newKey.length === 0">{{
                                                t('palceholderNoKey')
                                            }}</span>
                                        </label>
                                        <input
                                            id="newkey"
                                            v-model="newKey"
                                            type="text"
                                            name="newkey"
                                            autocomplete="off"
                                            class="block w-full rounded-md border shadow-sm focus:outline-hidden sm:text-sm"
                                            :class="
                                                conflict === true
                                                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                            "
                                            :placeholder="`${t('placeholderKey')}`"
                                            @input="conflictDetect(newKey)"
                                        />
                                    </div>
                                    <div class="mt-2">
                                        <label
                                            for="newvalue"
                                            class="block text-sm font-medium text-gray-700"
                                        >
                                            <span v-if="newValue.length === 0">{{
                                                t('placeholderNoValue')
                                            }}</span>
                                        </label>
                                        <input
                                            id="newValue"
                                            v-model="newValue"
                                            type="text"
                                            name="newvalue"
                                            autocomplete="off"
                                            class="block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                            :placeholder="`${t('placeholderKeyValue')}`"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="mt-5 sm:mt-4 sm:flex sm:justify-end sm:gap-2">
                                <ActionButton
                                    :value="t('buttonCancel')"
                                    state="neutral"
                                    :icon="MdiChevronLeft"
                                    @click="dialogClose()"
                                />
                                <ActionButton
                                    :value="t('buttonSave')"
                                    state="primary"
                                    :icon="MdiChevronDoubleDown"
                                    :is-disabled="newKey.length === 0 || conflict === true"
                                    @click="addKeyWithValue(newKey, newValue)"
                                />
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { ref, toRef } from 'vue';
import ActionButton from '../ui/ActionButton.vue';
import { t } from '../composables/i18n';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { MdiClose, MdiChevronLeft, MdiChevronDoubleDown } from 'materialdesignicons-vue3/icons/';

defineExpose({
    name: 'PlaceholderKeyDialog',
});

// not used const emit = defineEmits(['update:newKey']);

const props = defineProps({
    placeholder: {
        type: Object,
        required: true,
    },
    state: {
        type: Object,
        default: () => {
            return {
                open: false,
                title: `${t('placeholderNewKey')}`,
                overlayPreventClose: false,
            };
        },
    },
});

const placeholder = toRef(props, 'placeholder');
const newKey = ref('');
const newValue = ref('');
const state = props.state;
const conflict = ref(false);

const conflictDetect = (newKey) => {
    if (placeholder.value.model.includes(newKey)) {
        conflict.value = true;
    } else {
        conflict.value = false;
    }
};

const addKeyWithValue = (key, value) => {
    if (typeof placeholder.value.uiConfig !== 'object' || placeholder.value.uiConfig === null) {
        placeholder.value.uiConfig = {};
    }
    if (placeholder.value.sample.length === 0) {
        placeholder.value.sample.push({});
    }

    for (let i in placeholder.value.sample) {
        placeholder.value.sample[i][key] = '';
    }

    placeholder.value.uiConfig[key] = value;
    placeholder.value.model.push(key);
    dialogClose();
};

const dialogClose = (preventClose = false) => {
    if (preventClose === false) {
        state.open = false;
    }
};
</script>
